import { useEffect } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { useSessionStorage } from '@betterment/js-runtime';
import { H1, VStack } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { useNavigate } from 'react-router-dom';

import { AssignToExistingPurpose } from './components/AssignToExistingPurpose';
import { CreateNewPurposeButton } from './components/CreateNewPurposeButton';

import { ProductCategory, PurposeCreationIntent } from '../../../graphql/schema';
import Footer from '../../../Shared/components-v2/footer/Footer';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { usePurposeSelectionStep } from '../FlowConfig';
import { ExistingPurposesDocument } from '../graphql/query.existingPurposes.generated';

const SELECTION_TO_TYPED_PURPOSE = {
  emergency_fund: PurposeCreationIntent.EmergencyFund,
  retirement: PurposeCreationIntent.Retirement,
  major_purchase: PurposeCreationIntent.MajorPurchase,
};

export const AssignmentStep: React.FC = () => {
  const navigate = useNavigate();
  const { commit, dependencies } = usePurposeSelectionStep('assignment');
  const { data } = useGraphQLQuery(ExistingPurposesDocument, {
    variables: { intent: SELECTION_TO_TYPED_PURPOSE[dependencies.selection.purposeType] },
    throwOnError: true,
  });

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  useEffect(() => {
    if (data?.purposes.length === 0) {
      void navigate(urlBuilder.appRedirectToPurposeFlow({ purposeType: dependencies.selection.purposeType }), {
        replace: true,
      });
    }
  }, [data, navigate, dependencies]);

  if (!data || data.purposes.length === 0) {
    return null;
  }

  const handleNextStep = (purposeId: string) => {
    commit({ purposeId });

    void navigate(
      urlBuilder.appAddGoalFlowsNew({
        advice_type: dependencies.selection.purposeType,
        product_category: productCategory,
        envelope_id: purposeId,
      })
    );
  };

  return (
    <WorkflowLayout pageTitle="">
      <WorkflowLayout.Header workflowTitle={null} onBack={() => navigate(-1)} onClose={() => navigate('/')} />
      <WorkflowLayout.Content>
        <H1 variant="pageTitle" css={{ mb: 'space.300' }}>
          Add to your existing goal?
        </H1>
        <VStack css={{ gap: 'space.150', mb: 'space.150' }}>
          {data.purposes.map(purpose => (
            <AssignToExistingPurpose purpose={purpose} handleAssignment={handleNextStep} key={purpose.id} />
          ))}
          <CreateNewPurposeButton purposeType={dependencies.selection.purposeType} />
        </VStack>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
